import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheck, FiX, FiEye } from "react-icons/fi";
import { CiImageOn } from "react-icons/ci";
import { submissionSeriesEpisodesApi, qc_status } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import RejectModal from "./RejectModal";
import ConfirmDialog from "./ConfirmDialog";
import style from "./Submissions.module.css";
import detailStyle from "./SubmissionDetail.module.css";

const badgeClass = (qc) =>
  qc === "Approved" ? style.approved : qc === "Rejected" ? style.rejected : style.pending;

const fmtSubmitted = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

function Thumb({ src, alt }) {
  const [err, setErr] = useState(false);
  if (!src || err)
    return (
      <div className={style.thumb}>
        <CiImageOn size={22} />
      </div>
    );
  return (
    <div className={style.thumb}>
      <img src={src} alt={alt || ""} onError={() => setErr(true)} />
    </div>
  );
}

// Drill-down page a reviewer lands on after clicking a grouped Series row in
// Submissions — shows each episode individually so it can be approved/
// rejected on its own, then links into the same SubmissionDetail page used
// for a single video's full review.
const SubmissionSeries = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();

  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null); // { id, title }
  const [rejecting, setRejecting] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null); // { id, title }

  const load = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await submissionSeriesEpisodesApi(seriesId);
      if (!res?.data?.series) {
        setNotFound(true);
        setSeries(null);
      } else {
        setSeries(res.data.series);
        setEpisodes(Array.isArray(res.data.episodes) ? res.data.episodes : []);
      }
    } catch (e) {
      setNotFound(true);
      setSeries(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesId]);

  const openEpisode = (ep) => navigate(`/mainlayout/submissions/${ep.id}`);

  const doApprove = async ({ notify }) => {
    if (!approveTarget) return;
    setBusyId(approveTarget.id);
    try {
      await qc_status(approveTarget.id, { status: "Approved", notify });
      showToast("Episode approved", "success");
      setApproveTarget(null);
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to approve", "error");
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async ({ reason, note, notify }) => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await qc_status(rejectTarget.id, { status: "Rejected", reason, note, notify });
      showToast("Episode rejected", "success");
      setRejectTarget(null);
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to reject", "error");
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className={style.page}>
        <div className={style.stateBox}>Loading episodes…</div>
      </div>
    );
  }

  if (notFound || !series) {
    return (
      <div className={style.page}>
        <button
          className={detailStyle.backBtn}
          onClick={() => navigate("/mainlayout/submissions")}
        >
          <FiArrowLeft /> Back to submissions
        </button>
        <div className={style.stateBox}>Series not found.</div>
      </div>
    );
  }

  return (
    <div className={style.page}>
      <button
        className={detailStyle.backBtn}
        onClick={() => navigate("/mainlayout/submissions")}
      >
        <FiArrowLeft /> Back to submissions
      </button>

      <div className={style.header}>
        <h1 className={style.heading}>{series.name}</h1>
        <span className={style.headingCount}>
          {episodes.length} episode{episodes.length === 1 ? "" : "s"}
        </span>
      </div>

      {episodes.length === 0 ? (
        <div className={style.stateBox}>No episodes yet.</div>
      ) : (
        <div className={style.rows}>
          {episodes.map((ep) => {
            const isPending = (ep.qc_status || "Pending") === "Pending";
            const submitted = fmtSubmitted(ep.submission_date);
            const meta = [
              ep.season,
              ep.episode_number != null ? `Episode ${ep.episode_number}` : null,
              submitted ? `submitted ${submitted}` : null,
              `SUB-${ep.id}`,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div key={ep.id} className={style.row}>
                <span style={{ width: 18, flexShrink: 0 }} />
                <Thumb src={ep.portrait} alt={ep.name} />
                <div className={style.rowInfo} onClick={() => openEpisode(ep)}>
                  <div className={style.rowTitle}>{ep.name}</div>
                  <div className={style.rowMeta}>{meta}</div>
                </div>
                <span className={`${style.badge} ${badgeClass(ep.qc_status)}`}>
                  {ep.qc_status || "Pending"}
                </span>
                <div className={style.rowActions}>
                  {isPending && (
                    <>
                      <button
                        className={style.approveBtn}
                        disabled={busyId === ep.id}
                        onClick={() => setApproveTarget({ id: ep.id, title: ep.name })}
                      >
                        <FiCheck size={13} /> Approve
                      </button>
                      <button
                        className={style.rejectBtn}
                        disabled={busyId === ep.id}
                        onClick={() => setRejectTarget({ id: ep.id, title: ep.name })}
                      >
                        <FiX size={13} /> Reject
                      </button>
                    </>
                  )}
                  <button
                    className={style.eyeBtn}
                    title="Review"
                    onClick={() => openEpisode(ep)}
                  >
                    <FiEye size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RejectModal
        open={!!rejectTarget}
        title={rejectTarget?.title}
        submitting={rejecting}
        onCancel={() => setRejectTarget(null)}
        onConfirm={doReject}
      />

      <ConfirmDialog
        open={!!approveTarget}
        tone="success"
        confirmLabel="Approve"
        title="Approve episode"
        message={
          approveTarget
            ? `Approve "${approveTarget.title}"? Once approved it becomes live-ready.`
            : ""
        }
        showNotify
        submitting={busyId !== null}
        onCancel={() => setApproveTarget(null)}
        onConfirm={doApprove}
      />
    </div>
  );
};

export default SubmissionSeries;
